/**
 * Menu item prices are stored as integer minor units (`menu_items.price_cents`)
 * plus an ISO 4217 `currency`. These helpers convert between what a person
 * types into the price field and that storage form.
 */

/** Upper bound enforced by the `menu_items_price_cents_check` constraint. */
export const MAX_PRICE_CENTS = 100_000_000;

export const DEFAULT_MENU_CURRENCY = 'USD';

/**
 * Parses a typed price ("12", "12.5", "$12.50", "12,50", "1,250.00") into
 * cents. Returns `null` for an empty field (no price) and `undefined` when
 * the text is not a valid price (more than two decimals, letters, negative,
 * above `MAX_PRICE_CENTS`).
 */
export function parseCents(
  input: string | null | undefined
): number | null | undefined {
  const raw = (input ?? '').trim();
  if (!raw) return null;
  if (raw.includes('-')) return undefined;
  // Drop spaces and a leading/trailing currency symbol or code ("$", "USD").
  const cleaned = raw
    .replace(/\s/g, '')
    .replace(/^[^\d.,]+/, '')
    .replace(/[^\d.,]+$/, '');
  if (!/^[\d.,]+$/.test(cleaned) || !/\d/.test(cleaned)) return undefined;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  let integerPart = cleaned;
  let fractionPart = '';
  const decimalAt = Math.max(lastDot, lastComma);
  if (decimalAt !== -1) {
    const separator = cleaned[decimalAt];
    const after = cleaned.slice(decimalAt + 1);
    const other = separator === '.' ? ',' : '.';
    const separatorCount = cleaned.split(separator).length - 1;
    // "1,250" / "1.250.000": a lone separator followed by exactly three digits
    // (or a repeated one) is digit grouping, not a decimal point.
    const isGrouping =
      !cleaned.includes(other) &&
      (separatorCount > 1 || (after.length === 3 && separator === ','));
    if (!isGrouping) {
      if (after.length > 2 || separatorCount > 1) return undefined;
      integerPart = cleaned.slice(0, decimalAt);
      fractionPart = after;
    }
  }
  if (/[.,]/.test(integerPart)) {
    // Digit grouping must look like grouping: "1,250,000", never "1.2.3".
    const groups = integerPart.split(/[.,]/);
    const separators = new Set(integerPart.replace(/\d/g, ''));
    if (
      separators.size !== 1 ||
      !/^\d{1,3}$/.test(groups[0]) ||
      groups.slice(1).some(group => !/^\d{3}$/.test(group))
    ) {
      return undefined;
    }
    integerPart = groups.join('');
  }
  if (!/^\d*$/.test(integerPart) || !/^\d*$/.test(fractionPart)) {
    return undefined;
  }
  const cents =
    Number(integerPart || '0') * 100 +
    Number(fractionPart.padEnd(2, '0') || '0');
  if (!Number.isSafeInteger(cents) || cents < 0 || cents > MAX_PRICE_CENTS) {
    return undefined;
  }
  return cents;
}

/** Cents as an editable string without currency symbol ("12.50"). */
export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Localized display price ("$12.50"). Empty string when there is no price.
 * Falls back to the default currency when `currency` is not a valid ISO code.
 */
export function formatPrice(
  cents: number | null | undefined,
  currency: string | null | undefined = DEFAULT_MENU_CURRENCY,
  locale?: string
): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return '';
  }
  const code = normalizeCurrency(currency);
  const amount = cents / 100;
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${amount.toFixed(fractionDigits)} ${code}`;
  }
}

/** Upper-cased three-letter ISO code, or the default currency. */
export function normalizeCurrency(currency: string | null | undefined): string {
  const code = (currency ?? '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : DEFAULT_MENU_CURRENCY;
}
